import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TAccessManagementStore,
	TEmKeycloakUsersRequest,
	TEmKeycloakUsersResponsePayload,
	TKeycloakGroupsRequest,
	TKeycloakGroupsResponsePayload,
	TKeycloakUsersRequest,
	TKeycloakUsersResponsePayload,
	TAttachEmKeycloakUserRequest,
	TDetachEmKeycloakUserRequest,
	TCreateKeycloakUserRequest,
	TSetKeycloakUserEnabledRequest,
	TResetKeycloakUserPasswordRequest,
	TConnectedEmerchantsRequest,
	TConnectedEmerchantsResponsePayload,
} from './types';

const initialState: TAccessManagementStore = {
	emKeycloakUsers: null,
	emKeycloakUsersLoading: false,
	emKeycloakUsersFailure: false,
	keycloakGroups: null,
	keycloakGroupsLoading: false,
	keycloakUsers: null,
	keycloakUsersLoading: false,
	attachEmUserLoading: false,
	attachEmUserSuccess: false,
	detachEmUserLoading: false,
	detachEmUserSuccess: false,
	createEmUserLoading: false,
	createEmUserSuccess: false,
	setUserEnabledLoading: false,
	setUserEnabledSuccess: false,
	resetUserPasswordLoading: false,
	resetUserPasswordSuccess: false,
	connectedEmerchants: null,
	connectedEmerchantsLoading: false,
	connectedEmerchantsFailure: false,
};

const accessManagement = createSlice({
	name: '@@accessManagement',
	initialState,
	reducers: {
		getEmKeycloakUsersRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TEmKeycloakUsersRequest>,
		) => {
			const s = state;
			s.emKeycloakUsersLoading = true;
			s.emKeycloakUsersFailure = false;
		},
		getEmKeycloakUsersSuccess: (
			state: TAccessManagementStore,
			action: PayloadAction<TEmKeycloakUsersResponsePayload>,
		) => {
			const s = state;
			s.emKeycloakUsers = action.payload;
			s.emKeycloakUsersLoading = false;
			s.emKeycloakUsersFailure = false;
		},
		getEmKeycloakUsersFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.emKeycloakUsersLoading = false;
			s.emKeycloakUsers = null;
			s.emKeycloakUsersFailure = true;
		},

		getKeycloakGroupsRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TKeycloakGroupsRequest>,
		) => {
			const s = state;
			s.keycloakGroupsLoading = true;
		},
		getKeycloakGroupsSuccess: (
			state: TAccessManagementStore,
			action: PayloadAction<TKeycloakGroupsResponsePayload>,
		) => {
			const s = state;
			s.keycloakGroups = action.payload;
			s.keycloakGroupsLoading = false;
		},
		getKeycloakGroupsFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.keycloakGroupsLoading = false;
		},

		getKeycloakUsersRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TKeycloakUsersRequest>,
		) => {
			const s = state;
			s.keycloakUsersLoading = true;
		},
		getKeycloakUsersSuccess: (
			state: TAccessManagementStore,
			action: PayloadAction<TKeycloakUsersResponsePayload>,
		) => {
			const s = state;
			s.keycloakUsers = action.payload;
			s.keycloakUsersLoading = false;
		},
		getKeycloakUsersFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.keycloakUsersLoading = false;
			s.keycloakUsers = null;
		},

		// attach user to EM
		attachEmUserRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TAttachEmKeycloakUserRequest>,
		) => {
			const s = state;
			s.attachEmUserLoading = true;
			s.attachEmUserSuccess = false;
		},
		attachEmUserSuccess: (state: TAccessManagementStore) => {
			const s = state;
			s.attachEmUserLoading = false;
			s.attachEmUserSuccess = true;
		},
		attachEmUserFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.attachEmUserLoading = false;
			s.attachEmUserSuccess = false;
		},

		// detach user from EM
		detachEmUserRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TDetachEmKeycloakUserRequest>,
		) => {
			const s = state;
			s.detachEmUserLoading = true;
			s.detachEmUserSuccess = false;
		},
		detachEmUserSuccess: (state: TAccessManagementStore) => {
			const s = state;
			s.detachEmUserLoading = false;
			s.detachEmUserSuccess = true;
		},
		detachEmUserFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.detachEmUserLoading = false;
			s.detachEmUserSuccess = false;
		},

		// create user
		createKeycloakUserRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TCreateKeycloakUserRequest>,
		) => {
			const s = state;
			s.createEmUserLoading = true;
			s.createEmUserSuccess = false;
		},
		createKeycloakUserSuccess: (state: TAccessManagementStore) => {
			const s = state;
			s.createEmUserLoading = false;
			s.createEmUserSuccess = true;
		},
		createKeycloakUserFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.createEmUserLoading = false;
			s.createEmUserSuccess = false;
		},

		// set enabled
		setKeycloakUserEnabledRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TSetKeycloakUserEnabledRequest>,
		) => {
			const s = state;
			s.setUserEnabledLoading = true;
			s.setUserEnabledSuccess = false;
		},
		setKeycloakUserEnabledSuccess: (state: TAccessManagementStore) => {
			const s = state;
			s.setUserEnabledLoading = false;
			s.setUserEnabledSuccess = true;
		},
		setKeycloakUserEnabledFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.setUserEnabledLoading = false;
			s.setUserEnabledSuccess = false;
		},

		// reset password
		resetKeycloakUserPasswordRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TResetKeycloakUserPasswordRequest>,
		) => {
			const s = state;
			s.resetUserPasswordLoading = true;
			s.resetUserPasswordSuccess = false;
		},
		resetKeycloakUserPasswordSuccess: (state: TAccessManagementStore) => {
			const s = state;
			s.resetUserPasswordLoading = false;
			s.resetUserPasswordSuccess = true;
		},
		resetKeycloakUserPasswordFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.resetUserPasswordLoading = false;
			s.resetUserPasswordSuccess = false;
		},

		// connected emerchants
		getConnectedEmerchantsRequest: (
			state: TAccessManagementStore,
			action: PayloadAction<TConnectedEmerchantsRequest>,
		) => {
			const s = state;
			s.connectedEmerchantsLoading = true;
			s.connectedEmerchantsFailure = false;
		},
		getConnectedEmerchantsSuccess: (
			state: TAccessManagementStore,
			action: PayloadAction<TConnectedEmerchantsResponsePayload>,
		) => {
			const s = state;
			s.connectedEmerchants = action.payload;
			s.connectedEmerchantsLoading = false;
			s.connectedEmerchantsFailure = false;
		},
		getConnectedEmerchantsFailure: (state: TAccessManagementStore) => {
			const s = state;
			s.connectedEmerchantsLoading = false;
			s.connectedEmerchants = null;
			s.connectedEmerchantsFailure = true;
		},
	},
});

export default accessManagement.reducer;
export const {
	getEmKeycloakUsersRequest,
	getEmKeycloakUsersSuccess,
	getEmKeycloakUsersFailure,
	getKeycloakGroupsRequest,
	getKeycloakGroupsSuccess,
	getKeycloakGroupsFailure,
	getKeycloakUsersRequest,
	getKeycloakUsersSuccess,
	getKeycloakUsersFailure,
	attachEmUserRequest,
	attachEmUserSuccess,
	attachEmUserFailure,
	detachEmUserRequest,
	detachEmUserSuccess,
	detachEmUserFailure,
	createKeycloakUserRequest,
	createKeycloakUserSuccess,
	createKeycloakUserFailure,
	setKeycloakUserEnabledRequest,
	setKeycloakUserEnabledSuccess,
	setKeycloakUserEnabledFailure,
	resetKeycloakUserPasswordRequest,
	resetKeycloakUserPasswordSuccess,
	resetKeycloakUserPasswordFailure,
	getConnectedEmerchantsRequest,
	getConnectedEmerchantsSuccess,
	getConnectedEmerchantsFailure,
} = accessManagement.actions;
